import { Controller } from "@hotwired/stimulus"

// Controller for OAuth session debugging functionality
// Used in admin/session_debug/browser_check.html.erb
export default class extends Controller {
  static targets = ["cookies", "sessionAnalysis", "cookieMonitor", "serverCheck", "testResults"]
  static values = {
    sessionCookieName: { type: String, default: "_nexus_session" },
    inspectUrl: { type: String, default: "/admin/session_debug/inspect" },
    testSetUrl: { type: String, default: "/admin/session_debug/test_set" },
    testReadUrl: { type: String, default: "/admin/session_debug/test_read" }
  }

  connect() {
    this.lastCookies = document.cookie
    this.analyzeCookies()
    this.startCookieMonitor()
    this.startAutoServerCheck()
  }

  disconnect() {
    if (this.cookieMonitorInterval) {
      clearInterval(this.cookieMonitorInterval)
    }
    if (this.serverCheckInterval) {
      clearInterval(this.serverCheckInterval)
    }
  }

  analyzeCookies() {
    const cookies = document.cookie
    const cookieList = cookies.split("; ").map(c => {
      const [name, value] = c.split("=")
      return { name, value, length: value ? value.length : 0 }
    })

    this.cookiesTarget.textContent = JSON.stringify(cookieList, null, 2)

    // Analyze session cookie specifically
    const sessionCookie = cookieList.find(c => c.name === this.sessionCookieNameValue)
    const analysis = {
      found: !!sessionCookie,
      name: this.sessionCookieNameValue,
      valueLength: sessionCookie ? sessionCookie.length : 0,
      firstChars: sessionCookie ? sessionCookie.value.substring(0, 20) + "..." : "N/A",
      timestamp: new Date().toISOString()
    }

    this.sessionAnalysisTarget.textContent = JSON.stringify(analysis, null, 2)

    return sessionCookie
  }

  startCookieMonitor() {
    this.cookieMonitorInterval = setInterval(() => {
      const currentCookies = document.cookie
      if (currentCookies !== this.lastCookies) {
        const timestamp = new Date().toISOString()
        const message = `[${timestamp}] COOKIES CHANGED!\nOld: ${this.lastCookies}\nNew: ${currentCookies}\n`
        this.cookieMonitorTarget.textContent += message
        this.lastCookies = currentCookies
        this.analyzeCookies()
      }
    }, 1000)
  }

  startAutoServerCheck() {
    this.serverCheckInterval = setInterval(() => {
      this.checkServerSession()
    }, 5000)
  }

  async checkServerSession() {
    try {
      const response = await fetch(this.inspectUrlValue, {
        credentials: "include",
        headers: {
          "Accept": "application/json",
          "X-Debug-Request": "browser-check"
        }
      })

      const data = await response.json()
      this.serverCheckTarget.textContent = JSON.stringify(data, null, 2)

      // Highlight discrepancies
      const browserSession = this.analyzeCookies()
      if (browserSession && data.session && data.session.id) {
        if (!document.cookie.includes(data.session.id)) {
          alert("WARNING: Server session ID not found in browser cookies!")
        }
      }
    } catch (error) {
      this.serverCheckTarget.textContent = `Error: ${error.message}`
    }
  }

  async testSetSession() {
    try {
      const testKey = "browser_test_" + Date.now()
      const testValue = "test_value_" + Math.random()
      const csrfToken = document.querySelector("[name=\"csrf-token\"]")?.content || ""

      const response = await fetch(this.testSetUrlValue, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken
        },
        body: `key=${testKey}&value=${testValue}`
      })

      const data = await response.json()
      this.testResultsTarget.textContent =
        `SET TEST:\n${JSON.stringify(data, null, 2)}\n\n` +
        `Browser cookies after set: ${document.cookie}\n`

      // Check if cookie changed
      this.analyzeCookies()
    } catch (error) {
      this.testResultsTarget.textContent = `Set Error: ${error.message}`
    }
  }

  async testReadSession() {
    try {
      const response = await fetch(this.testReadUrlValue, {
        credentials: "include",
        headers: {
          "Accept": "application/json"
        }
      })

      const data = await response.json()
      this.testResultsTarget.textContent =
        `READ TEST:\n${JSON.stringify(data, null, 2)}\n\n` +
        `Browser cookies during read: ${document.cookie}\n`
    } catch (error) {
      this.testResultsTarget.textContent = `Read Error: ${error.message}`
    }
  }
}
